import type {
  FetchPrecondition,
  FetchPreconditionValue,
} from '@de/providers/DataSourceManager.types';
import { pathSegments, readOwnPath } from '@de/utils/readOwnPath';

export type { FetchPrecondition };

/** Raw input roots a precondition may read. */
export const FETCH_PRECONDITION_ROOTS: ReadonlySet<string> = new Set([
  'context',
  'formState',
]);

/**
 * Roots that name a decision's answer. A precondition decides what a
 * decision is given, so reading an answer would make the decision depend on
 * itself or on another decision.
 */
const OUTCOME_ROOTS: ReadonlySet<string> = new Set([
  'decision',
  'decisions',
  'outcome',
  'outcomes',
  'outcomeId',
  'variant',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isPreconditionValue = (value: unknown): value is FetchPreconditionValue =>
  typeof value === 'string' ||
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value));

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

export type FetchPreconditionCheck =
  | { readonly ok: true; readonly precondition: FetchPrecondition }
  | { readonly ok: false; readonly reason: string };

/**
 * Validates an authored precondition. A precondition reads a raw input
 * (`context` or `formState`) and compares it with one of the decision
 * service's operators. One that reads a decision outcome is refused, as is
 * one that reads another data source: either would have to wait for a fetch
 * or an evaluation before it could decide whether to fetch.
 */
export function checkFetchPrecondition(raw: unknown): FetchPreconditionCheck {
  if (!isRecord(raw)) {
    return { ok: false, reason: 'A precondition must be an object.' };
  }
  const { source, path, op } = raw;
  if (!isNonEmptyString(source) || !isNonEmptyString(path)) {
    return { ok: false, reason: 'A precondition needs a source and a path.' };
  }
  const root = source.trim();
  const trimmedPath = path.trim();
  // The same segments the reader follows, so a leading `.` or `[0]` cannot
  // hide which root the precondition really reads.
  const segments = pathSegments(trimmedPath);
  if (segments.length === 0) {
    return { ok: false, reason: 'A precondition needs a source and a path.' };
  }
  const firstSegment = segments[0];
  if (OUTCOME_ROOTS.has(root) || OUTCOME_ROOTS.has(firstSegment)) {
    return {
      ok: false,
      reason: `A precondition may not read a decision outcome ("${root}:${trimmedPath}"); a binding cannot depend on a decision.`,
    };
  }
  if (!FETCH_PRECONDITION_ROOTS.has(root)) {
    return {
      ok: false,
      reason: `A precondition reads a raw input ("context" or "formState"), not "${root}".`,
    };
  }
  const base = {
    source: root as FetchPrecondition['source'],
    path: trimmedPath,
  };
  switch (op) {
    case 'eq':
    case 'neq':
      if (!isPreconditionValue(raw.value)) {
        return {
          ok: false,
          reason: `Operator "${op}" needs a string, number, or boolean value.`,
        };
      }
      return { ok: true, precondition: { ...base, op, value: raw.value } };
    case 'in':
    case 'notIn':
      if (
        !Array.isArray(raw.values) ||
        raw.values.length === 0 ||
        !raw.values.every(isPreconditionValue)
      ) {
        return {
          ok: false,
          reason: `Operator "${op}" needs a non-empty list of values.`,
        };
      }
      return {
        ok: true,
        precondition: { ...base, op, values: [...raw.values] },
      };
    case 'isNull':
    case 'isNotNull':
      return { ok: true, precondition: { ...base, op } };
    default:
      return {
        ok: false,
        reason: `Operator "${String(op)}" is not one of eq, neq, in, notIn, isNull, isNotNull.`,
      };
  }
}

/**
 * Why an authored `fetchWhen` list is refused, or `null` when every
 * precondition in it is allowed. One refused precondition refuses the whole
 * list: the source must not be fetched on its say-so, whichever side runs it.
 */
export function fetchWhenRefusal(fetchWhen: unknown): string | null {
  if (fetchWhen === undefined || fetchWhen === null) return null;
  if (!Array.isArray(fetchWhen)) return 'its fetchWhen is not a list.';
  for (const raw of fetchWhen) {
    const check = checkFetchPrecondition(raw);
    if ('reason' in check) return check.reason;
  }
  return null;
}

/** True when one precondition holds against the raw input roots. */
export function preconditionHolds(
  precondition: FetchPrecondition,
  roots: Readonly<Record<string, unknown>>,
): boolean {
  const value = Object.prototype.hasOwnProperty.call(roots, precondition.source)
    ? readOwnPath(roots[precondition.source], precondition.path)
    : undefined;
  const present = value !== null && value !== undefined;
  switch (precondition.op) {
    case 'eq':
      return present && value === precondition.value;
    case 'neq':
      return present && value !== precondition.value;
    case 'in':
      return present && precondition.values.some((item) => item === value);
    case 'notIn':
      return present && !precondition.values.some((item) => item === value);
    case 'isNull':
      return !present;
    case 'isNotNull':
      return present;
    default:
      return false;
  }
}

/**
 * True when the source may fetch: no preconditions, or at least one holds.
 * Two bindings that need the same source for different reasons each add a
 * precondition, and the source is fetched once when either needs it.
 */
export function fetchWhenSatisfied(
  fetchWhen: readonly FetchPrecondition[] | undefined,
  roots: Readonly<Record<string, unknown>>,
): boolean {
  if (!fetchWhen || fetchWhen.length === 0) return true;
  return fetchWhen.some((precondition) =>
    preconditionHolds(precondition, roots),
  );
}

/**
 * How a list of preconditions stands when only some `context` keys are known:
 *
 * - `holds`: no preconditions, or one whose inputs are known holds.
 * - `fails`: every precondition has known inputs, and none holds.
 * - `unknown`: none with known inputs holds, and one reads a `context` key
 *   that is not available, so the answer depends on input this side lacks.
 *
 * A key that is present with a null value is known: `isNull` holds on it.
 * Only a key that is not there at all is unknown.
 */
export type PreconditionStanding = 'holds' | 'fails' | 'unknown';

export function fetchWhenStanding(
  fetchWhen: readonly FetchPrecondition[] | undefined,
  roots: Readonly<Record<string, unknown>>,
  availableContextKeys: ReadonlySet<string>,
): PreconditionStanding {
  if (!fetchWhen || fetchWhen.length === 0) return 'holds';
  let unknown = false;
  for (const precondition of fetchWhen) {
    if (
      precondition.source === 'context' &&
      !availableContextKeys.has(pathSegments(precondition.path)[0] ?? '')
    ) {
      unknown = true;
      continue;
    }
    if (preconditionHolds(precondition, roots)) return 'holds';
  }
  return unknown ? 'unknown' : 'fails';
}

/** True when a precondition reads `formState.<key>`. */
export function fetchWhenReadsFormKey(
  fetchWhen: readonly FetchPrecondition[] | undefined,
  key: string,
): boolean {
  return (fetchWhen ?? []).some(
    (precondition) =>
      precondition.source === 'formState' &&
      pathSegments(precondition.path)[0] === key,
  );
}
